<template>
  <v-dialog
    v-model="showDialog"
    max-width="600px"
    :fullscreen="$vuetify.breakpoint.smAndDown"
    :scrollable="$vuetify.breakpoint.mdAndUp"
    transition="dialog-bottom-transition"
    eager
  >
    <v-card>
      <v-toolbar
        v-if="$vuetify.breakpoint.smAndDown"
        color="primary"
        text
        dark
      >
        <v-btn
          icon
          dark
          @click="showDialog = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>

        <v-toolbar-title>
          {{ editModel.isNew() ? 'New step' : `Edit ${editModel.name}` }}
        </v-toolbar-title>

        <v-spacer />

        <v-toolbar-items v-if="canEdit">
          <v-btn
            v-if="!editModel.isNew()"
            text
            color="error"
            :loading="editModel.saving"
            :disabled="editModel.saving"
            @click="remove()"
          >
            Delete
          </v-btn>

          <v-btn
            dark
            text
            :loading="editModel.saving"
            :disabled="editModel.saving"
            @click="save"
          >
            {{ editModel.isNew() ? 'Create' : 'Save' }}
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>

      <v-card-title v-else>
        {{ editModel.isNew() ? 'New step' : `Edit ${editModel.name}` }}
      </v-card-title>

      <v-divider v-if="$vuetify.breakpoint.mdAndUp" />

      <v-card-text :style="$vuetify.breakpoint.mdAndUp ? 'height:400px' : ''">
        <v-form
          ref="form"
          v-model="valid"
        >
          <v-container grid-list-xl>
            <v-layout wrap>
              <v-flex
                xs12
                md6
              >
                <v-text-field
                  v-model="editModel.name"
                  label="Name"
                  required
                />
              </v-flex>

              <v-flex
                xs12
                md6
              >
                <v-text-field
                  ref="mapAutocomplete"
                  :value="editModel.location"
                  label="Location"
                  prepend-icon="mdi-map-marker"
                />
              </v-flex>

              <v-flex
                xs12
                md6
              >
                <v-menu
                  v-model="startDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      :value="fmtDate(editModel.startDate)"
                      label="Start Date *"
                      readonly
                      prepend-icon="mdi-calendar"
                      :error-messages="editModel.errors.startDate"
                      clearable
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="editModel.startDate"
                    @change="startDateMenu = false"
                  />
                </v-menu>
              </v-flex>

              <v-flex
                xs12
                md6
              >
                <v-menu
                  v-model="endDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      :value="fmtDate(editModel.endDate)"
                      label="End Date"
                      readonly
                      prepend-icon="mdi-calendar"
                      clearable
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="editModel.endDate"
                    :allowed-dates="allowedEndDate"
                    @change="endDateMenu = false"
                  />
                </v-menu>
              </v-flex>

              <v-flex
                xs12
                md6
              >
                <v-text-field
                  v-model.number="editModel.cost"
                  :error-messages="editModel.errors.cost"
                  label="Cost"
                  prefix="€"
                  placeholder="0.00"
                  required
                />
              </v-flex>

              <v-flex
                xs12
                md6
              >
                <v-text-field
                  v-model.number="editModel.url"
                  :error-messages="editModel.errors.url"
                  label="Web page"
                  prepend-icon="mdi-link"
                  placeholder="www..."
                  required
                />
              </v-flex>

              <vu-file-upload
                v-if="false"
                accept="*/*"
                type="file"
                :firestore-ref="stepRef"
                firestore-field="attachment"
                :storage-path="editModel.tripId"
              />

              <v-flex
                xs12
              >
                <span class="subheading">Type</span>

                <v-chip-group
                  v-model="editModel.type"
                  column
                  active-class="accent--text"
                >
                  <v-chip
                    v-for="type in allStepTypes"
                    :key="type.value"
                    :value="type.value"
                  >
                    <v-icon left>
                      {{ type.icon }}
                    </v-icon>
                    {{ type.text }}
                  </v-chip>
                </v-chip-group>
              </v-flex>
            </v-layout>
          </v-container>
        </v-form>
      </v-card-text>

      <v-divider v-if="$vuetify.breakpoint.mdAndUp" />

      <v-card-actions
        v-if="$vuetify.breakpoint.mdAndUp && canEdit"
      >
        <v-btn
          v-if="!editModel.isNew()"
          text
          color="error"
          :loading="editModel.saving"
          :disabled="editModel.saving"
          @click="remove()"
        >
          Delete
        </v-btn>

        <v-spacer />

        <v-btn
          text
          color="primary"
          :loading="editModel.saving"
          :disabled="editModel.saving"
          @click="save"
        >
          {{ editModel.isNew() ? 'Create' : 'Save' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { allStepTypes } from './TripStepTypes'
import VuFileUpload from '@/components/VuFileUpload'
import { fmtDate } from '@/plugins/filters'
import { firestore } from '@/plugins/firebase'

export default {
  components: {
    VuFileUpload
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    editModel: {
      type: Object,
      required: true
    },
    trip: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      allStepTypes,
      endDateMenu: false,
      startDateMenu: false,
      valid: true
    }
  },
  computed: {
    canEdit () {
      if (this.$currentUser) {
        const role = this.trip.users[this.$currentUser.id]
        return role === 'owner' || role === 'editor'
      } else {
        return false
      }
    },
    showDialog: {
      get () { return this.show },
      set (val) { this.$emit('update:show', val) }
    },
    stepRef () {
      return this.editModel.id ? firestore.collection('trip-steps').doc(this.editModel.id) : null
    }
  },
  mounted () {
    /* global google */
    // eslint-disable-next-line no-new
    const autocomplete = new google.maps.places.Autocomplete(
      this.$refs.mapAutocomplete.$el.querySelector('input'),
      {
        // types: [ 'geocode' ]
      })

    autocomplete.setFields([ 'address_components', 'geometry' ])

    // workaround to remove Automplete placeholder
    this.$refs.mapAutocomplete.$el.querySelector('input').setAttribute('placeholder', '')

    google.maps.event.addListener(autocomplete, 'place_changed', () => {
      const place = autocomplete.getPlace()

      for (const comp of place.address_components) {
        if (comp.types[0] === 'country') {
          this.editModel.country = comp.short_name
        }
      }

      this.editModel.location = place.address_components[0].long_name
      this.editModel.lat = place.geometry.location.lat()
      this.editModel.lng = place.geometry.location.lng()
    })
  },
  methods: {
    fmtDate,
    save () {
      this.editModel
        .save()
        .then(() => {
          this.showDialog = false
          this.$emit('save')
        })
    },
    remove () {
      this.editModel
        .delete()
        .then(() => {
          this.showDialog = false
        })
    },
    allowedEndDate (val) {
      if (this.editModel.startDate) {
        return val > this.editModel.startDate
      } else {
        return true
      }
    }
  }
}
</script>

<style>

</style>
